/**
 * StockSalesPage — Allocating Stock → Sales.
 *
 * Mirror Access form `frmMatchingPSMatch_Stock_Sales` + VBA module.
 * Layout 3-col đúng Access:
 *   Top header row: Date | (spacer) | Matching with different quality + checkbox
 *   Grid 3 cột:     Sales (Short Side) | Middle (Allocating ID/KG/Note) | Stock (Long Side)
 *   Toolbar row:    Title + 5 buttons
 *   Datasheet:      subForm
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { type Dayjs } from 'dayjs'

import ConfirmDialog from '@/components/ConfirmDialog'
import { useRowSelection } from '@/components/dataList'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePermission } from '@/hooks/usePermission'
import {
  useDeleteStockSalesMatchMutation,
  useGetStockSalesContractOptionsQuery,
  useGetStockSalesRowsQuery,
  useGetStockSalesStackOptionsQuery,
  useLazyGetStockSalesAggregatesQuery,
  useLazyGetStockSalesSalesMetaQuery,
  useLazyGetStockSalesStackInfoQuery,
  useSaveStockSalesMatchMutation,
  type StockSalesContractOption,
  type StockSalesStackOption,
} from '@/store/api/matchingPsApi'

import ActionToolbar from './ActionToolbar'
import MiddleColumnPanel from './MiddleColumnPanel'
import SalesSidePanel from './SalesSidePanel'
import StockSalesDataGrid from './StockSalesDataGrid'
import StockSidePanel from './StockSidePanel'
import { formatAccessDate, toNumber } from './stockSalesUtils'

const FUNCTION_KEY = 'matching.stock_sales'

export default function StockSalesPage() {
  const { canEdit } = usePermission(FUNCTION_KEY)
  const snackbar = useSnackbar()

  /* ───────── Header state (form cha — unbound textboxes) ───────── */
  const [matchId, setMatchId] = useState<number | null>(null)
  const [matchDate, setMatchDate] = useState<Dayjs | null>(() => dayjs())
  const [sSide, setSSide] = useState('')
  const [sWarehouse, setSWarehouse] = useState('')
  const [sClient, setSClient] = useState('')
  const [sQuality, setSQuality] = useState('')
  const [sQuantity, setSQuantity] = useState<number | null>(null)
  const [sDeliveryDate, setSDeliveryDate] = useState('')
  const [sTotalMatched, setSTotalMatched] = useState<number | null>(null)
  const [sMatched, setSMatched] = useState<number | null>(null)
  const [sUnmatched, setSUnmatched] = useState<number | null>(null)
  const [stockStack, setStockStack] = useState('')
  const [stockDateIn, setStockDateIn] = useState('')
  const [stockWarehouse, setStockWarehouse] = useState('')
  const [stockQuality, setStockQuality] = useState('')
  const [stockQuantity, setStockQuantity] = useState<number | null>(null)
  const [stockMatched, setStockMatched] = useState<number | null>(null)
  const [stockUnmatched, setStockUnmatched] = useState<number | null>(null)
  const [matchKg, setMatchKg] = useState<string>('')
  const [note, setNote] = useState('')
  const [checkedQuality, setCheckedQuality] = useState(false)

  /* ───────── Datasheet selection ───────── */
  const {
    selectedId: selectedMatchId,
    setSelectedId: setSelectedMatchId,
    toggleSelect: toggleSelectMatchId,
  } = useRowSelection<number>()

  /* ───────── Confirm Unallocate dialog ───────── */
  const [confirmOpen, setConfirmOpen] = useState(false)

  /* ───────── Data fetching ───────── */
  const datasheet = useGetStockSalesRowsQuery()
  const contractOpts = useGetStockSalesContractOptionsQuery()
  const stackOpts = useGetStockSalesStackOptionsQuery(
    sWarehouse || sQuality
      ? {
          warehouse: sWarehouse || undefined,
          quality: !checkedQuality && sQuality ? sQuality : undefined,
        }
      : undefined,
  )

  const [fetchSalesMeta] = useLazyGetStockSalesSalesMetaQuery()
  const [fetchAggregates] = useLazyGetStockSalesAggregatesQuery()
  const [fetchStackInfo] = useLazyGetStockSalesStackInfoQuery()

  const [saveMatch, { isLoading: isSaving }] = useSaveStockSalesMatchMutation()
  const [deleteMatch, { isLoading: isDeleting }] = useDeleteStockSalesMatchMutation()

  /* ───────── Memoized selected option objects ───────── */
  const contractValue = useMemo<StockSalesContractOption | null>(() => {
    if (!sSide) return null
    const found = (contractOpts.data ?? []).find(
      (o) => o.s_side === sSide && (sWarehouse ? o.stock_side === sWarehouse : true),
    )
    if (found) return found
    return {
      s_side: sSide,
      stock_side: sWarehouse,
      quality: sQuality || null,
      client_short: sClient || null,
      merge_value: `${sSide} - ${sWarehouse}`,
    }
  }, [contractOpts.data, sSide, sWarehouse, sQuality, sClient])

  const stackValue = useMemo<StockSalesStackOption | null>(() => {
    if (!stockStack) return null
    const found = (stackOpts.data ?? []).find((o) => o.stack_id === stockStack)
    if (found) return found
    return {
      stack_id: stockStack,
      warehouse_short: null,
      warehouse: stockWarehouse || null,
      quality_code: stockQuality || null,
      date_in: null,
      stack_can_allocated: stockQuantity,
      stack_allocated: stockMatched,
      stack_unallocated: stockUnmatched,
    }
  }, [
    stackOpts.data,
    stockStack,
    stockWarehouse,
    stockQuality,
    stockQuantity,
    stockMatched,
    stockUnmatched,
  ])

  /* ───────── Helpers ───────── */

  const resetSalesFields = useCallback(() => {
    setSSide('')
    setSWarehouse('')
    setSClient('')
    setSQuality('')
    setSQuantity(null)
    setSDeliveryDate('')
    setSTotalMatched(null)
    setSMatched(null)
    setSUnmatched(null)
  }, [])

  const resetStockFields = useCallback(() => {
    setStockStack('')
    setStockDateIn('')
    setStockWarehouse('')
    setStockQuality('')
    setStockQuantity(null)
    setStockMatched(null)
    setStockUnmatched(null)
  }, [])

  const resetAllFields = useCallback(() => {
    setMatchId(null)
    setMatchDate(dayjs())
    setMatchKg('')
    resetSalesFields()
    resetStockFields()
  }, [resetSalesFields, resetStockFields])

  /* ───────── Sales aggregate hydration ───────── */

  const hydrateSalesAggregates = useCallback(
    async (
      args: { sSideValue: string; warehouseValue: string; subtractKg?: number } = {
        sSideValue: '',
        warehouseValue: '',
      },
    ) => {
      const { sSideValue, warehouseValue, subtractKg = 0 } = args
      if (!sSideValue) {
        setSTotalMatched(null)
        setSMatched(null)
        setSUnmatched(null)
        return
      }
      try {
        const agg = await fetchAggregates({
          sSide: sSideValue,
          warehouse: warehouseValue || undefined,
        }).unwrap()
        const total = toNumber(agg.total_matched)
        const matched = toNumber(agg.s_allocated) - subtractKg
        setSTotalMatched(total)
        setSMatched(matched)
        setSUnmatched(total - matched)
      } catch {
        setSTotalMatched(null)
        setSMatched(null)
        setSUnmatched(null)
      }
    },
    [fetchAggregates],
  )

  /* ───────── Default match_kg ← min(s_unmatched, stock_unmatched) ───────── */
  const lastAutoKgRef = useRef<number | null>(null)
  useEffect(() => {
    if (sUnmatched == null || stockUnmatched == null) return
    const next = Math.max(0, Math.min(sUnmatched, stockUnmatched))
    if (next !== lastAutoKgRef.current) {
      lastAutoKgRef.current = next
      setMatchKg(String(next))
    }
  }, [sUnmatched, stockUnmatched])

  /* ───────── Handlers ───────── */

  const handleClean = useCallback(() => {
    resetAllFields()
    setNote('')
    lastAutoKgRef.current = null
  }, [resetAllFields])

  const handleContractChange = useCallback(
    async (opt: StockSalesContractOption | null) => {
      if (!opt) {
        resetSalesFields()
        return
      }
      setSSide(opt.s_side)
      setSWarehouse(opt.stock_side)

      try {
        const meta = await fetchSalesMeta({ sSide: opt.s_side }).unwrap()
        setSClient(meta.client_short ?? '')
        setSQuality(meta.quality ?? '')
        setSQuantity(meta.quantity == null ? null : Number(meta.quantity))
        setSDeliveryDate(meta.delivery_to ? formatAccessDate(meta.delivery_to) : '')
        await hydrateSalesAggregates({
          sSideValue: opt.s_side,
          warehouseValue: opt.stock_side,
        })
        if (
          (meta.quality &&
            stockQuality &&
            meta.quality !== stockQuality &&
            opt.s_side !== '') ||
          (opt.stock_side &&
            stockWarehouse &&
            opt.stock_side !== stockWarehouse)
        ) {
          resetStockFields()
        }
      } catch {
        snackbar.error('Failed to load contract metadata.')
      }
    },
    [
      fetchSalesMeta,
      hydrateSalesAggregates,
      resetSalesFields,
      resetStockFields,
      stockQuality,
      stockWarehouse,
      snackbar,
    ],
  )

  const handleStackChange = useCallback(
    async (opt: StockSalesStackOption | null) => {
      if (!opt) {
        resetStockFields()
        if (sSide) {
          await hydrateSalesAggregates({
            sSideValue: sSide,
            warehouseValue: sWarehouse,
          })
        }
        return
      }
      try {
        const info = await fetchStackInfo({ stackId: opt.stack_id }).unwrap()
        setStockStack(opt.stack_id)
        setStockQuantity(info.stack_can_allocated ?? null)
        setStockQuality(info.quality_code ?? '')
        setStockWarehouse(info.warehouse ?? '')
        setStockDateIn(info.date_in ? formatAccessDate(info.date_in) : '')
        setStockMatched(info.stack_allocated ?? null)
        setStockUnmatched(info.stack_unallocated ?? null)
      } catch {
        snackbar.error('Failed to load stack info.')
      }
    },
    [fetchStackInfo, hydrateSalesAggregates, resetStockFields, sSide, sWarehouse, snackbar],
  )

  const handleCheckedQualityToggle = useCallback(
    async (checked: boolean) => {
      setCheckedQuality(checked)
      if (sSide) {
        await hydrateSalesAggregates({ sSideValue: sSide, warehouseValue: sWarehouse })
      }
    },
    [hydrateSalesAggregates, sSide, sWarehouse],
  )

  const handleLoad = useCallback(async () => {
    if (selectedMatchId == null) return
    const row = (datasheet.data ?? []).find((r) => Number(r.match_id) === selectedMatchId)
    if (!row) {
      snackbar.warning('Row no longer in datasheet — please Refresh.')
      return
    }
    const rowSSide = String(row.s_side ?? '')
    const rowWh = String(row.s_warehouse ?? '')
    const rowKg = toNumber(row.match_kg)
    const rowStack = String(row.stack_side ?? '')

    setMatchId(selectedMatchId)
    setMatchDate(row.match_date ? dayjs(row.match_date as string) : dayjs())
    setMatchKg(String(rowKg))
    setSSide(rowSSide)
    setSWarehouse(rowWh)
    setStockStack(rowStack)

    let metaQuality = ''
    try {
      const meta = await fetchSalesMeta({ sSide: rowSSide }).unwrap()
      metaQuality = meta.quality ?? ''
      setSClient(meta.client_short ?? '')
      setSQuality(metaQuality)
      setSQuantity(meta.quantity == null ? null : Number(meta.quantity))
      setSDeliveryDate(meta.delivery_to ? formatAccessDate(meta.delivery_to) : '')
    } catch {
      setSClient('')
      setSQuality('')
      setSQuantity(null)
      setSDeliveryDate('')
    }

    await hydrateSalesAggregates({
      sSideValue: rowSSide,
      warehouseValue: rowWh,
      subtractKg: rowKg,
    })

    try {
      const info = await fetchStackInfo({ stackId: rowStack }).unwrap()
      setStockQuantity(info.stack_can_allocated ?? null)
      setStockQuality(info.quality_code ?? '')
      setStockWarehouse(info.warehouse ?? '')
      setStockDateIn(info.date_in ? formatAccessDate(info.date_in) : '')
      const stkAllocated = toNumber(info.stack_allocated) - rowKg
      const stkUnalloc = toNumber(info.stack_unallocated) + rowKg
      setStockMatched(stkAllocated)
      setStockUnmatched(stkUnalloc)

      if (metaQuality && info.quality_code && metaQuality !== info.quality_code) {
        setCheckedQuality(true)
      } else {
        setCheckedQuality(false)
      }
    } catch {
      resetStockFields()
    }
    lastAutoKgRef.current = rowKg
  }, [
    selectedMatchId,
    datasheet.data,
    fetchSalesMeta,
    fetchStackInfo,
    hydrateSalesAggregates,
    resetStockFields,
    snackbar,
  ])

  const handleRefresh = useCallback(() => {
    void datasheet.refetch()
  }, [datasheet])

  const handleSave = useCallback(async () => {
    if (!canEdit) return
    if (!matchDate || !matchDate.isValid()) {
      snackbar.info('Please type Matching Date!')
      return
    }
    if (!sSide) {
      snackbar.info('Please choose Contract Sales!')
      return
    }
    if (!stockStack) {
      snackbar.info('Please choose Stack ID!')
      return
    }
    const kg = toNumber(matchKg)
    if (
      kg <= 0 ||
      (sUnmatched != null && kg > sUnmatched) ||
      (stockUnmatched != null && kg > stockUnmatched)
    ) {
      snackbar.info('Matching KG is invalid!')
      return
    }

    try {
      const result = await saveMatch({
        matchId,
        matchDate: matchDate.format('YYYY-MM-DD'),
        sSide,
        stackSide: stockStack,
        matchKg: kg,
        note,
      }).unwrap()
      snackbar.success(result.message)
      void datasheet.refetch()
      resetAllFields()
      setNote('')
      lastAutoKgRef.current = null
    } catch (err) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ?? 'Failed to save.'
      snackbar.error(msg)
    }
  }, [
    canEdit,
    matchDate,
    sSide,
    stockStack,
    matchKg,
    sUnmatched,
    stockUnmatched,
    saveMatch,
    matchId,
    note,
    datasheet,
    resetAllFields,
    snackbar,
  ])

  const handleDeleteRequest = useCallback(() => {
    if (!canEdit) return
    if (matchId == null) return
    setConfirmOpen(true)
  }, [canEdit, matchId])

  const handleDeleteConfirm = useCallback(async () => {
    if (matchId == null) return
    try {
      const result = await deleteMatch({ matchId }).unwrap()
      snackbar.success(result.message)
      void datasheet.refetch()
      resetAllFields()
      setNote('')
      lastAutoKgRef.current = null
    } catch (err) {
      const msg =
        (err as { data?: { detail?: string } })?.data?.detail ?? 'Failed to unmatch.'
      snackbar.error(msg)
    } finally {
      setConfirmOpen(false)
    }
  }, [deleteMatch, matchId, datasheet, resetAllFields, snackbar])

  /* ───────── Render ───────── */

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Tách khung nội dung khỏi thanh tab phía trên (đồng bộ p:1 của tab khác).
        pt: 0.75,
      }}
    >
      {/* Top header row (Access T=60): Date ở cột Sales + checkbox ở cột Stock,
          align theo grid 3-col bên dưới. */}
      <Box
        sx={{
          px: 1,
          pt: 0.25,
          pb: 0.25,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.9fr 1.2fr' },
          gap: 0.75,
          alignItems: 'center',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              width: 130,
              flexShrink: 0,
              fontSize: '0.78rem',
              fontWeight: 700,
            }}
          >
            Date
          </Typography>
          <DatePicker
            value={matchDate}
            onChange={setMatchDate}
            format="DD-MMM-YYYY"
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                InputProps: { sx: { fontSize: '0.8rem' } },
                sx: { '& .MuiInputBase-input': { py: 0.5, fontWeight: 700 } },
              },
            }}
          />
        </Stack>
        <Box />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              sx={{ py: 0 }}
              checked={checkedQuality}
              onChange={(e) => void handleCheckedQualityToggle(e.target.checked)}
            />
          }
          label="Matching with different quality"
          slotProps={{
            typography: {
              sx: { fontSize: '0.78rem', fontWeight: 700, color: '#17365D' },
            },
          }}
          sx={{ m: 0 }}
        />
      </Box>

      {/* 3-col body grid (Access L=204/2856 | L=6540 | L=9588/12012) */}
      <Box
        sx={{
          px: 1,
          pb: 0.25,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.9fr 1.2fr' },
          gap: 0.75,
        }}
      >
        <SalesSidePanel
          contractOptions={contractOpts.data ?? []}
          contractValue={contractValue}
          onContractChange={handleContractChange}
          sClient={sClient}
          sQuality={sQuality}
          sQuantity={sQuantity}
          sDeliveryDate={sDeliveryDate}
          sTotalMatched={sTotalMatched}
          sMatched={sMatched}
          sUnmatched={sUnmatched}
        />
        <MiddleColumnPanel
          matchId={matchId}
          matchKg={matchKg}
          onMatchKgChange={setMatchKg}
          note={note}
          onNoteChange={setNote}
        />
        <StockSidePanel
          stackOptions={stackOpts.data ?? []}
          stackValue={stackValue}
          onStackChange={handleStackChange}
          stockDateIn={stockDateIn}
          stockWarehouse={stockWarehouse}
          stockQuality={stockQuality}
          stockQuantity={stockQuantity}
          stockMatched={stockMatched}
          stockUnmatched={stockUnmatched}
        />
      </Box>

      <StockSalesDataGrid
        selectedMatchId={selectedMatchId}
        onSelect={(next) => (next == null ? setSelectedMatchId(null) : toggleSelectMatchId(next))}
        title="Matching Stock to Sales"
        actions={
          <ActionToolbar
            canEdit={canEdit}
            hasSelectedRow={selectedMatchId != null}
            isEditing={matchId != null}
            isSaving={isSaving}
            isDeleting={isDeleting}
            onClean={handleClean}
            onLoad={handleLoad}
            onSave={handleSave}
            onRefresh={handleRefresh}
            onDelete={handleDeleteRequest}
          />
        }
      />

      <ConfirmDialog
        open={confirmOpen}
        title="Unallocate S-Stock"
        message={`Do you want to unmatch this Matching ID = '${matchId ?? ''}'?`}
        confirmLabel="Unmatch"
        danger
        loading={isDeleting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  )
}
